import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../Config/axiosInstance';
import './AddProjectBudget.css';
import IconButton from '@mui/material/IconButton';
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Select from 'react-select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import makeAnimated from 'react-select/animated';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { NumericFormat } from "react-number-format";

const formatDateLabel = (dateString) => {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("-");
  return `${day}.${month}.${year}`;
};

function AddBudgetStructure() {
  const [rows, setRows] = useState([]);
  const animatedComponents = makeAnimated();
  const [selectedSource, setSelectedSource] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    cost_element_description: "",
    cost_element_id: "",
    offer_code_id: "",
  });

  const [offerCodes, setOfferCodes] = useState([]);
  const [costs, setCosts] = useState([]);
  const [selectedOfferCode, setSelectedOfferCode] = useState(null);
  const [sources, setSources] = useState([]);
  const [externalSuppliers, setExternalSuppliers] = useState([]);
  const [internalSuppliers, setInternalSuppliers] = useState([]);
  const [pricePerHourList, setPricePerHourList] = useState([]);

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

  const filteredCosts = selectedOfferCode
    ? costs.filter((cost_element) => cost_element.offer_code_id === selectedOfferCode)
    : [];

  useEffect(() => {
    axiosInstance.get("/offer-codes/")
      .then(response => {
        const formatted = response.data.map(of => ({
          value: of.id,
          label: `${of.offer_code} - ${of.description}`,
        }));
        setOfferCodes(formatted);
      })
      .catch(error => console.error("Error fetching offer codes:", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/budget-structure/")
      .then(response => {
        const formatted = response.data.map(de => ({
          value: de.id,
          label: `${de.cost_element_id} - ${de.cost_element_description}`,
          cost_element_id: de.cost_element_id,
          offer_code_id: de.offer_code_details.id,
        }));
        setCosts(formatted);
      })
      .catch(error => console.error("Error fetching costs:", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/source-types-project-budget/")
      .then(response => {
        const formattedSources = response.data.map(src => ({
          value: src.value,
          label: src.label,
        }));
        setSources(formattedSources);
      })
      .catch(error => console.error("Error fetching source types:", error));
  }, []);

  useEffect(() => {
    const fetchSuppliersForUser = async () => {
      try {
        const departmentResponse = await axiosInstance.get("/department/user/");
        const accessibleDepartments = departmentResponse.data.map(dep => dep.value);

        const statusSupplierDeptResponse = await axiosInstance.get("/status-supplier-department/");
        const supplierDepartments = statusSupplierDeptResponse.data;

        const allowedSupplierIds = supplierDepartments
          .filter(item =>
            item.status === "Active" &&
            accessibleDepartments.includes(item.department)
          )
          .map(item => item.supplier);

        const suppliersResponse = await axiosInstance.get("/suppliers/");
        const accessibleSuppliers = suppliersResponse.data.filter(supplier =>
          allowedSupplierIds.includes(supplier.id)
        );

        const supplierOptions = accessibleSuppliers.map(supplier => ({
          value: supplier.id,
          label: supplier.name,
        }));

        setExternalSuppliers(supplierOptions);
      } catch (error) {
        console.error("Eroare la preluarea supplierilor:", error);
      }
    };

    fetchSuppliersForUser();
  }, []);

  useEffect(() => {
    axiosInstance.get('/internal-resources/')
      .then(response => {
        const activeInternalResources = response.data.filter(ir => ir.status === 'Activ');
        const formattedInternalResources = activeInternalResources.map(ir => ({
          value: ir.id,
          label: `${ir.resource_name}`
        }));
        setInternalSuppliers(formattedInternalResources);
      })
      .catch(error => console.error("Error fetching internal resources:", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/currencies/")
      .then((response) => {
        const formattedCurrencies = response.data.map((currency) => ({
          value: currency.id,
          label: `${currency.abbreviation}`,
        }));
        setCurrencyOptions(formattedCurrencies);
      })
      .catch((error) => console.error("Error fetching currencies:", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/exchange-rate/")
      .then((response) => {
        setExchangeRates(response.data || []);
      })
      .catch((error) => console.error("Error fetching exchange rates:", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/system-currency/")
      .then((response) => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setSystemCurrencyId(response.data[0]?.currency || null);
        }
      })
      .catch((error) => console.error("Error fetching system currency:", error));
  }, []);

  useEffect(() => {
    if (!systemCurrencyId || !currencyOptions.length) return;

    const systemCurrency = currencyOptions.find(
      currency => Number(currency.value) === Number(systemCurrencyId)
    );

    setSystemCurrencyLabel(systemCurrency?.label || "");
  }, [systemCurrencyId, currencyOptions]);

  useEffect(() => {
    axiosInstance.get("/price-per-hour/")
      .then(response => {
        const formattedData = response.data.map(item => {
          const currency = currencyOptions.find(c => c.value === item.currency) || {};
          return {
            id: item.id || "",
            pricePerHour: item.price_per_hour,
            internalResourceId: item.internal_resource || "",
            date: item.date || "",
            internalResource: item.internal_resource_name,
            currencyId: item.currency || "",
            currencyAbbreviation: currency.label || "",
          };
        });
        setPricePerHourList(formattedData);
      })
      .catch(error => console.error("Error fetching price per hour:", error));
  }, [currencyOptions]);

  const calculateCostWithoutVAT = (nr_hours, pricePerHour) => {
    if (!nr_hours || !pricePerHour) return 0;
    return Number(nr_hours) * Number(pricePerHour);
  };

  const getExchangeOptionsForCurrency = (currencyId) => {
    return exchangeRates
      .filter(rate => Number(rate.currency) === Number(currencyId))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(rate => ({
        value: rate.id,
        label: String(rate.value ?? ""),
        rateValue: Number(rate.value),
        date: rate.date,
      }));
  };

  const calculateSystemCost = (cost, currencyId, selectedExchangeRate) => {
    if (cost === "" || cost === null || cost === undefined) return "";

    const numericCost = Number(cost);
    if (!Number.isFinite(numericCost)) return "";

    if (!currencyId || !systemCurrencyId) return "";

    if (Number(currencyId) === Number(systemCurrencyId)) {
      return numericCost.toFixed(2);
    }

    if (!selectedExchangeRate?.rateValue) return "";

    return (numericCost * selectedExchangeRate.rateValue).toFixed(2);
  };

  const updateRowSystemCost = (row) => ({
    ...row,
    cost_offer_without_vat_system: calculateSystemCost(
      row.cost_without_VAT,
      row.currency,
      row.selectedExchangeRate
    ),
  });

  const addRow = () => {
    if (!selectedSource) return;

    if (rows.length > 0) {
      setConfirmDialogOpen(true);
      return;
    }

    addNewRow();
  };

  const addNewRow = () => {
    const newRow = {
      id: Date.now(),
      source: selectedSource,
      supplier: "",
      nr_hours: "",
      nr_hours_po: "",
      cost_without_VAT: "",
      cost_PO_without_VAT: "",
      price_per_hour_id: "",
      internal_supplier: "",
      external_supplier: "",
      material_supplier: "",
      selectedInternalSupplier: null,
      currency: "",
      exchange_rate: "",
      selectedExchangeRate: null,
      cost_offer_without_vat_system: "",
      revenue_without_vat: "",
      revenue_po_without_vat: "",
    };

    setRows([...rows, newRow]);
    setShowModal(false);
    setSelectedSource("");
    setConfirmDialogOpen(false);
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    const currentRow = updatedRows[index];

    if (field === "supplier" && currentRow.source === "Internal") {
      updatedRows[index] = {
        ...currentRow,
        supplier: value,
        selectedInternalSupplier: value,
        price_per_hour_id: "",
        cost_without_VAT: "",
        cost_offer_without_vat_system: "",
      };
    }

    else if (field === "price_per_hour_id") {
      const selectedPrice = pricePerHourList.find(item => item.id === value);

      const newCost = calculateCostWithoutVAT(
        currentRow.nr_hours,
        selectedPrice?.pricePerHour || 0
      );

      updatedRows[index] = updateRowSystemCost({
        ...currentRow,
        [field]: value,
        cost_without_VAT: newCost
      });
    }

    else if (field === "nr_hours") {
      const selectedPrice = pricePerHourList.find(
        item => item.id === currentRow.price_per_hour_id
      );

      const newCost = calculateCostWithoutVAT(
        value,
        selectedPrice?.pricePerHour || 0
      );

      updatedRows[index] = updateRowSystemCost({
        ...currentRow,
        [field]: value,
        cost_without_VAT: newCost
      });
    }

    else if (field === "cost_without_VAT") {
      updatedRows[index] = updateRowSystemCost({
        ...currentRow,
        [field]: value
      });
    }

    else if (field === "currency") {
      const isSystemCurrency =
        systemCurrencyId && Number(value) === Number(systemCurrencyId);

      const defaultExchangeRate = !isSystemCurrency
        ? getExchangeOptionsForCurrency(value)[0] || null
        : null;

      updatedRows[index] = updateRowSystemCost({
        ...currentRow,
        currency: value,
        exchange_rate: defaultExchangeRate?.value || "",
        selectedExchangeRate: defaultExchangeRate,
      });
    }

    else if (field === "exchange_rate") {
      updatedRows[index] = updateRowSystemCost({
        ...currentRow,
        exchange_rate: value?.value || "",
        selectedExchangeRate: value || null,
      });
    }

    else {
      updatedRows[index] = {
        ...currentRow,
        [field]: value
      };
    }

    setRows(updatedRows);
  };

  const renderCostField = (row, index, className = "form-group-cost") => (
    <div className={className}>
      <label htmlFor={`cost_without_VAT-${index}`} className="label">
        Cost offer without VAT<span style={{ color: 'red' }}>*</span>
      </label>

      <NumericFormat
        id={`cost_without_VAT-${index}`}
        name="cost_without_VAT"
        value={row.cost_without_VAT}
        onValueChange={({ value }) =>
          handleChange(index, "cost_without_VAT", value || "")
        }
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={2}
        fixedDecimalScale
        allowNegative={false}
        allowLeadingZeros={false}
        inputMode="decimal"
        className="input"
        placeholder="0,00"
        required
      />
    </div>
  );

  const renderCurrencyAndConversionFields = (row, index) => {
    const selectedCurrencyIsSystem =
      row.currency &&
      systemCurrencyId &&
      Number(row.currency) === Number(systemCurrencyId);

    const exchangeOptions = row.currency
      ? getExchangeOptionsForCurrency(row.currency)
      : [];

    return (
      <>
        <div className="form-group-currency">
          <label htmlFor={`currency-${index}`} className="label">
            Currency <span style={{ color: 'red' }}>*</span>
          </label>

          <Select
            id={`currency-${index}`}
            name="currency"
            classNamePrefix="react-select"
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === row.currency) || null}
            onChange={(selectedOption) => {
              handleChange(index, "currency", selectedOption ? selectedOption.value : "");
            }}
            components={animatedComponents}
            isClearable
            styles={{
              control: (provided) => ({
                ...provided,
                borderRadius: "8px",
              }),
            }}
            required
          />
        </div>

        <div className="form-group-currency">
          <label htmlFor={`exchange_rate-${index}`} className="label">
            Exchange Rate
            {!selectedCurrencyIsSystem && row.currency ? (
              <span style={{ color: 'red' }}> *</span>
            ) : null}
          </label>

          <Select
            id={`exchange_rate-${index}`}
            name="exchange_rate"
            classNamePrefix="react-select"
            options={exchangeOptions}
            value={row.selectedExchangeRate}
            onChange={(selectedOption) => {
              handleChange(index, "exchange_rate", selectedOption);
            }}
            components={animatedComponents}
            isClearable
            isDisabled={!row.currency || selectedCurrencyIsSystem}
            placeholder={
              !row.currency
                ? "Select currency first"
                : selectedCurrencyIsSystem
                  ? "Not needed"
                  : exchangeOptions.length
                    ? "Select rate"
                    : "No rates"
            }
            formatOptionLabel={(option, { context }) =>
              context === "menu"
                ? `${option.label}${option.date ? ` - ${formatDateLabel(option.date)}` : ""}`
                : option.label
            }
            styles={{
              control: (provided) => ({
                ...provided,
                borderRadius: "8px",
              }),
            }}
            required={!!row.currency && !selectedCurrencyIsSystem}
          />
        </div>

        <div className="form-group-cost">
          <label htmlFor={`cost_offer_without_vat_system-${index}`} className="label">
            Cost offer without VAT System
            {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
          </label>

          <NumericFormat
            id={`cost_offer_without_vat_system-${index}`}
            name="cost_offer_without_vat_system"
            value={row.cost_offer_without_vat_system}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            allowLeadingZeros={false}
            inputMode="decimal"
            className="input"
            placeholder="0,00"
            readOnly
          />
        </div>
      </>
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      await axiosInstance.get(`/costs/${formData.cost_element_description}/`);
    } catch (error) {
      alert(`Cost ${formData.cost_element_description} nu există!`);
      return;
    }

    const requestData = rows.map((row) => {
      const selectedPrice = pricePerHourList.find(
        item => item.id === row.price_per_hour_id
      );

      const baseData = {
        cost_element: Number(formData.cost_element_description),
        source_type: row.source,

        number_of_hours:
          row.source === "Internal"
            ? parseFloat(row.nr_hours) || 0
            : null,

        number_offer_without_vat: parseFloat(row.nr_hours_po) || null,

        cost_offer_without_vat: parseFloat(row.cost_without_VAT) || 0,

        cost_po_without_vat: parseFloat(row.cost_PO_without_VAT) || null,

        price_per_hour:
          row.source === "Internal" && selectedPrice
            ? parseFloat(selectedPrice.pricePerHour) || null
            : null,

        revenue_without_vat: parseFloat(row.revenue_without_vat) || 0,
        revenue_po_without_vat: parseFloat(row.revenue_po_without_vat) || 0,

        currency: row.currency ? Number(row.currency) : null,

        exchange_rate:
          row.exchange_rate && Number(row.currency) !== Number(systemCurrencyId)
            ? Number(row.exchange_rate)
            : null,
      };

      switch (row.source) {
        case "Internal":
          return {
            ...baseData,
            internal_resource: Number(row.supplier),
            supplier: null,
          };

        case "External":
          return {
            ...baseData,
            internal_resource: null,
            supplier: Number(row.supplier),
          };

        case "Material":
          return {
            ...baseData,
            internal_resource: null,
            supplier: Number(row.supplier),
          };

        case "Additional Internal":
          return {
            ...baseData,
            internal_resource: null,
            supplier: null,
          };

        default:
          return baseData;
      }
    });

    try {
      for (const data of requestData) {
        await axiosInstance.post('/project-budget/', data);
      }

      navigate('/project-budget');
    } catch (error) {
      console.error("Detalii eroare:", {
        request: error.config?.data,
        response: error.response?.data,
        status: error.response?.status
      });

      const errorMessage = error.response?.data?.message
        || error.response?.data
        || error.message;

      alert(`Eroare la salvare: ${JSON.stringify(errorMessage)}`);
    }
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  return (
    <div className='add-project-budget-page'>
      <div className="add-container">
        <div className="header">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon className="back-arrow" />
          </IconButton>

          <h2 className="add-title">Add budget</h2>
        </div>
      </div>

      <hr className="title-separator" />

      <div className="add-container">
        <form className="add-form" onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="offer_code" className="label">
              Offer Code <span style={{ color: "red" }}>*</span>
            </label>

            <Select
              id="offer_code"
              name="offer_code"
              classNamePrefix="react-select"
              options={offerCodes}
              required
              value={offerCodes.find(c => c.value === selectedOfferCode) || null}
              onChange={selectedOption => {
                const value = selectedOption ? selectedOption.value : null;
                setSelectedOfferCode(value);
                setFormData(prev => ({
                  ...prev,
                  cost_element_description: "",
                  cost_element_id: ""
                }));
              }}
              components={animatedComponents}
              isClearable
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderRadius: "8px",
                }),
              }}
            />
          </div>

          <div className="form-group-row">
            <div className="form-group-deliverable-number">
              <label htmlFor="cost_element_id" className="label">
                ID<span style={{ color: "red" }}>*</span>
              </label>

              <input
                id="cost_element_id"
                type="text"
                name="cost_element_id"
                value={formData.cost_element_id}
                className="input"
                disabled
                required
                readOnly
              />
            </div>

            <div className="form-group-deliverable-description">
              <label htmlFor="costs" className="label">
                Cost Description <span style={{ color: "red" }}>*</span>
              </label>

              <Select
                id="cost_element_description"
                name="cost_element_description"
                classNamePrefix="react-select"
                options={filteredCosts}
                required
                value={filteredCosts.find(c => c.value === formData.cost_element_description) || null}
                onChange={(selectedOption) => {
                  const selectedCost = filteredCosts.find(
                    (d) => d.value === selectedOption?.value
                  );

                  setFormData({
                    ...formData,
                    cost_element_description: selectedOption ? selectedOption.value : "",
                    cost_element_id: selectedCost ? selectedCost.cost_element_id : "",
                  });
                }}
                components={animatedComponents}
                isClearable
                isDisabled={!selectedOfferCode}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "8px",
                  }),
                }}
              />
            </div>

            <div className="form-group">
              <Button
                className="supplier-button"
                variant="contained"
                onClick={() => setShowModal(true)}
              >
                Add Supplier
              </Button>
            </div>
          </div>

          {showModal && (
            <div className="form-group-row">
              <div className="form-group-source-type">
                <label htmlFor="source_type" className="label">
                  Source Type <span style={{ color: "red" }}>*</span>
                </label>

                <Select
                  id="source_type"
                  name="source_type"
                  classNamePrefix="react-select"
                  options={sources}
                  required
                  value={sources.find(c => c.value === selectedSource) || null}
                  onChange={selectedOption => {
                    const value = selectedOption ? selectedOption.value : null;
                    setSelectedSource(value);
                  }}
                  components={animatedComponents}
                  isClearable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderRadius: "8px",
                    }),
                  }}
                />
              </div>

              <div className="form-group-buttons-wrapper">
                <div className="form-group-button">
                  <IconButton
                    onClick={addRow}
                    disabled={!selectedSource}
                    color={selectedSource ? "primary" : "default"}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </div>

                <div className="form-group-button">
                  <IconButton onClick={() => setShowModal(false)} color="error">
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          )}

          {rows.map((row, index) => (
            <div className="budget-row-wrapper" key={row.id}>
              <div className="budget-row-content">
                <div className="form-group-row budget-row-main">
                  <div className="form-group-source">
                    <label htmlFor={`source-${index}`}>
                      Source<span style={{ color: 'red' }}>*</span>
                    </label>

                    <input
                      type="text"
                      id={`source-${index}`}
                      name="source"
                      value={row.source}
                      disabled
                      required
                      className='input'
                      readOnly
                    />
                  </div>

                  {row.source === "Internal" && (
                    <>
                      <div className="form-group-supplier-internal">
                        <label htmlFor={`internal_supplier-${index}`} className="label">
                          Supplier <span style={{ color: 'red' }}>*</span>
                        </label>

                        <Select
                          id={`internal_supplier-${index}`}
                          name="internal_supplier"
                          classNamePrefix="react-select"
                          options={internalSuppliers}
                          value={internalSuppliers.find(option => option.value === row.supplier) || null}
                          onChange={(selectedOption) => {
                            const value = selectedOption ? selectedOption.value : null;
                            handleChange(index, "supplier", value);
                          }}
                          components={animatedComponents}
                          isClearable
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              borderRadius: "8px",
                            }),
                          }}
                          required
                        />
                      </div>

                      <div className="form-group-nr-hour">
                        <label htmlFor={`nr_hours-${index}`} className="label">
                          Nr. of hours <span style={{ color: 'red' }}>*</span>
                        </label>

                        <NumericFormat
                          id={`nr_hours-${index}`}
                          name="nr_hours"
                          value={row.nr_hours}
                          onValueChange={({ value }) =>
                            handleChange(index, "nr_hours", value || "")
                          }
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          allowLeadingZeros={false}
                          inputMode="decimal"
                          className="input"
                          placeholder="0,00"
                          required
                        />
                      </div>

                      <div className="form-group-price">
                        <label htmlFor={`price_per_hour-${index}`} className="label">
                          Price per Hour<span style={{ color: 'red' }}>*</span>
                        </label>

                        <Select
                          id={`price_per_hour-${index}`}
                          name="price_per_hour"
                          options={pricePerHourList.filter(item =>
                            item.internalResourceId === row.selectedInternalSupplier
                          )}
                          getOptionValue={(option) => option.id}
                          value={pricePerHourList.find(opt =>
                            opt.id === row.price_per_hour_id &&
                            opt.internalResourceId === row.selectedInternalSupplier
                          ) || null}
                          onChange={(selectedOption) => {
                            handleChange(index, "price_per_hour_id", selectedOption ? selectedOption.id : "");
                          }}
                          components={animatedComponents}
                          isClearable
                          required
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              borderRadius: "8px",
                            }),
                          }}
                          formatOptionLabel={(option, { context }) =>
                            context === "menu"
                              ? `${option.pricePerHour} ${option.currencyAbbreviation} - ${option.date} - ${option.internalResource}`
                              : `${option.pricePerHour} ${option.currencyAbbreviation}`
                          }
                        />
                      </div>
                    </>
                  )}

                  {(row.source === "External" || row.source === "Material") && (
                    <div className="form-group-supplier">
                      <label htmlFor={`external_supplier-${index}`} className="label">
                        Supplier <span style={{ color: 'red' }}>*</span>
                      </label>

                      <Select
                        id={`external_supplier-${index}`}
                        name="external_supplier"
                        classNamePrefix="react-select"
                        options={externalSuppliers}
                        value={externalSuppliers.find(option => option.value === row.supplier) || null}
                        onChange={(selectedOption) => {
                          handleChange(index, "supplier", selectedOption ? selectedOption.value : "");
                        }}
                        components={animatedComponents}
                        isClearable
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderRadius: "8px",
                          }),
                        }}
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="form-group-row budget-row-amounts">
                  {row.source === "Internal" &&
                    renderCostField(row, index, "form-group-cost-internal")}

                  {(row.source === "External" || row.source === "Material") &&
                    renderCostField(row, index, "form-group-cost")}

                  {row.source === "Additional Internal" &&
                    renderCostField(row, index, "form-group-cost-additional-internal")}

                  {renderCurrencyAndConversionFields(row, index)}
                </div>
              </div>

              <div className="budget-delete">
                <IconButton
                  onClick={() => handleDeleteRow(index)}
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          ))}

          <div className="form-group-row-button">
            <div className="button-group-inline">
              <button
                type="button"
                className="button discard"
                onClick={() => navigate(-1)}
              >
                Discard
              </button>

              <button type="submit" className="button save">
                Save
              </button>
            </div>
          </div>
        </form>
      </div>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        className="custom-dialog"
      >
        <DialogTitle className="custom-dialog-title">
          Confirmation
        </DialogTitle>

        <DialogContent className="custom-dialog-content">
          Are you sure you want to add more resources to this cost?
        </DialogContent>

        <DialogActions className="custom-dialog-actions">
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            className="cancel-button"
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            onClick={addNewRow}
            className="confirm-button"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AddBudgetStructure;